import { apiRequest } from './client'
import type {
  RfqLineDto,
  SupplierRfqInboxItemDto,
  SupplierRfqInboxPageDto,
  SupplierRfqRejectionReasonCode,
} from './types'

type RfqLineRaw = RfqLineDto & {
  product_type_id?: number
  sort_order?: number
  supplier_sku?: string | null
  product_name?: string | null
  line_status?: string
}

type SupplierInboxItemRaw = Partial<SupplierRfqInboxItemDto> & {
  inbox_id?: number
  rfq_id?: number
  buyer_organization_id?: number
  buyer_organization_name?: string
  buyer_legal_name?: string | null
  buyer_trade_name?: string | null
  restaurant_organization_id?: number
  restaurant_organization_name?: string
  restaurant_legal_name?: string | null
  restaurant_trade_name?: string | null
  rfq_title?: string
  rfq_description?: string | null
  rfq_status?: string
  inbox_status?: string
  decision_status?: string
  decision_comment?: string | null
  decision_at?: string | null
  completed_at?: string | null
  created_at?: string
  desired_delivery_date?: string | null
  desired_delivery_time_until?: string | null
  delivery_address?: string | null
  rejection_reason_code?: string | null
  substitution_comment?: string | null
  buyer_substitution_comment?: string | null
  restaurant_substitution_comment?: string | null
  customer_confirmed_at?: string | null
  customer_dispute_at?: string | null
  customer_dispute_comment?: string | null
  lines?: RfqLineRaw[]
}

const normalizeLine = (raw: RfqLineRaw): RfqLineDto => ({
  id: raw.id,
  productTypeId: raw.productTypeId ?? raw.product_type_id ?? 0,
  quantity: typeof raw.quantity === 'string' ? Number(raw.quantity) : raw.quantity,
  unit: raw.unit,
  note: raw.note ?? null,
  sortOrder: raw.sortOrder ?? raw.sort_order ?? 0,
  supplierSku: raw.supplierSku ?? raw.supplier_sku ?? null,
  productName: raw.productName ?? raw.product_name ?? null,
  lineStatus: raw.lineStatus ?? raw.line_status ?? 'active',
})

const normalizeInboxItem = (raw: SupplierInboxItemRaw): SupplierRfqInboxItemDto => ({
  inboxId: raw.inboxId ?? raw.inbox_id ?? 0,
  rfqId: raw.rfqId ?? raw.rfq_id ?? 0,
  buyerOrganizationId:
    raw.buyerOrganizationId
    ?? raw.buyer_organization_id
    ?? raw.restaurant_organization_id
    ?? 0,
  buyerOrganizationName:
    raw.buyerOrganizationName
    ?? raw.buyer_organization_name
    ?? raw.restaurant_organization_name
    ?? 'Покупатель',
  buyerLegalName:
    raw.buyerLegalName
    ?? raw.buyer_legal_name
    ?? raw.restaurant_legal_name
    ?? null,
  buyerTradeName:
    raw.buyerTradeName
    ?? raw.buyer_trade_name
    ?? raw.restaurant_trade_name
    ?? null,
  rfqTitle: raw.rfqTitle ?? raw.rfq_title ?? 'Заявка',
  rfqDescription: raw.rfqDescription ?? raw.rfq_description ?? null,
  rfqStatus: raw.rfqStatus ?? raw.rfq_status ?? 'open',
  inboxStatus: raw.inboxStatus ?? raw.inbox_status ?? 'unread',
  decisionStatus: raw.decisionStatus ?? raw.decision_status ?? 'pending',
  decisionComment: raw.decisionComment ?? raw.decision_comment ?? null,
  decisionAt: raw.decisionAt ?? raw.decision_at ?? null,
  completedAt: raw.completedAt ?? raw.completed_at ?? null,
  createdAt: raw.createdAt ?? raw.created_at ?? new Date(0).toISOString(),
  desiredDeliveryDate: raw.desiredDeliveryDate ?? raw.desired_delivery_date ?? null,
  desiredDeliveryTimeUntil:
    raw.desiredDeliveryTimeUntil ?? raw.desired_delivery_time_until ?? null,
  deliveryAddress: raw.deliveryAddress ?? raw.delivery_address ?? null,
  rejectionReasonCode: raw.rejectionReasonCode ?? raw.rejection_reason_code ?? null,
  substitutionComment: raw.substitutionComment ?? raw.substitution_comment ?? null,
  buyerSubstitutionComment:
    raw.buyerSubstitutionComment
    ?? raw.buyer_substitution_comment
    ?? raw.restaurant_substitution_comment
    ?? null,
  customerConfirmedAt: raw.customerConfirmedAt ?? raw.customer_confirmed_at ?? null,
  customerDisputeAt: raw.customerDisputeAt ?? raw.customer_dispute_at ?? null,
  customerDisputeComment: raw.customerDisputeComment ?? raw.customer_dispute_comment ?? null,
  lines: (raw.lines ?? []).map(normalizeLine),
})

export const listSupplierInbox = async (
  organizationId: number,
  page = 0,
  size = 50,
): Promise<SupplierRfqInboxPageDto> => {
  const pageDto = await apiRequest<SupplierRfqInboxPageDto & { items?: SupplierInboxItemRaw[] }>(
    `/supplier/rfqs/inbox?organizationId=${organizationId}&page=${page}&size=${size}`,
  )
  return {
    ...pageDto,
    items: (pageDto.items ?? []).map(normalizeInboxItem),
  }
}

export const decideSupplierRfq = (
  inboxId: number,
  organizationId: number,
  action: 'accept' | 'reject' | 'propose_substitution',
  options?: { message?: string; reasonCode?: SupplierRfqRejectionReasonCode },
) =>
  apiRequest<void>(`/supplier/rfqs/inbox/${inboxId}/decision?organizationId=${organizationId}`, {
    method: 'PATCH',
    body: {
      action,
      message: options?.message?.trim() || undefined,
      reason_code: options?.reasonCode,
    },
  })

export const completeSupplierRfq = (
  inboxId: number,
  organizationId: number,
  message?: string,
) =>
  apiRequest<void>(`/supplier/rfqs/inbox/${inboxId}/complete?organizationId=${organizationId}`, {
    method: 'PATCH',
    body: { message: message?.trim() || undefined },
  })
